import { createReadonlyProvider } from 'apprise-frontend-core/state/factory';

// helper kit for libraries that let apps exclude certain fields or tabs from their forms.
// the task involves boilerplate: take optional config from apps, merge it with defaults, store it in context, and use context to selectively render forms or tabs.

// the helper provides:
// - the config type FormExcludes<T extends string>. 
// - a hook that test for specific exclusions, and/or filter out keys from arbitratry records.
// - a factory function that generates a provider -- optionally with presets -- and returns it along with hook.
// the provider and the hook are built-in to share a context.

// libraries:
// - specialise T for added type safety (optional) and add PropertyExcludes<T> to their own config.
// - call the generating function in module scope, mount the generated provider, and export (and rename) the hook.
// - invoke the hook with the generated context and test if a tab should be included and/or to filter records of fields to render in forms. 


export type FormExcludes<T extends string = string> = {

    properties: T | T[]

}


export const createFormExcludes = <T extends string = string>(props?: FormExcludes<T>) => {

    const [ExcludeProvider, useBaseExcludes] = createReadonlyProvider<FormExcludes>(props ?? { properties: [] })

    const useExcludes = () => {

        const { properties } = useBaseExcludes()


        const self = {

            includedOf: <R extends Record<string, any>>(properties: R) =>

                Object.entries(properties).reduce((acc, e) => ({ ...acc, ...!self.isExcluded(e[0] as any) ? { [e[0]]: e[1] } : {} }), {}) as Partial<R>

            ,

            isExcluded: (property: T) => properties.includes(property)

        }

        return self
    }

    return [ExcludeProvider, useExcludes] as [typeof ExcludeProvider, typeof useExcludes]


}
